// 9fbef606107a605d69c0edbcd8029e5d
/* eslint-disable extra-rules/no-commented-out-code */
/* eslint-disable sonarjs/cognitive-complexity */
import React from 'react';
import PropTypes from 'prop-types';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import { getFlagIconPath, getLocator, isSNJ } from '@tcp/core/src/utils';
import DynamicModule from '@tcp/core/src/components/common/atoms/DynamicModule';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import HeaderLabels, { GLOBAL_LABELS } from '../../../labels/HeaderNew.labels';
import style from '../styles/CountrySelector.style';
import DynamicInjectCountrySelector from '../container/DynamicInjectCountrySelector';

/**
 * @class CountrySelector - Invokes a country, language, currency change selector
 * from the global header and footer
 * A Modal will be opened by clicking on flag icon or language from header and footer.
 * @param {props} accepts countriesMap, currenciesMap and languageMap as props.
 */
class CountrySelector extends React.Component {
  globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, HeaderLabels);

  loadCountryModuleXData = () => {
    const { getModuleXContent } = this.props;
    const referredContent = getDynamicLabelValues('global.countrySelector.referred');
    let content;
    if (referredContent && Array.isArray(referredContent)) {
      content = referredContent.find((label) => label.name === 'lbl_global_country_selector_note');
    }
    getModuleXContent(content && content.contentId);
  };

  openModal = () => {
    requestAnimationFrame(async () => {
      const { toggleModal } = this.props;
      await DynamicInjectCountrySelector();
      toggleModal({ isModalOpen: true });
    });
  };

  closeModal = () => {
    requestAnimationFrame(() => {
      const { toggleModal } = this.props;
      toggleModal({ isModalOpen: false });
    });
  };

  getCountryListData = () => {
    const { loadCountryListData } = this.props;
    loadCountryListData();
  };

  getSelectedCountry = (countryCode) => {
    const { countriesMap } = this.props;
    return countriesMap.find((country) => country.id === countryCode);
  };

  getCurrencyMap = (countryCode) => {
    const { countriesMap } = this.props;
    return countriesMap.find((country) => country.id === countryCode).currencyId;
  };

  getSelectedCurrency = (currencyCode) => {
    const { currenciesMap } = this.props;
    return currenciesMap.find((currency) => currency.id === currencyCode);
  };

  submitForm = () => {
    const {
      handleSubmit,
      country,
      currency,
      language,
      savedCountry,
      savedCurrency,
      savedLanguage,
    } = this.props;
    const selectedCurrency = this.getSelectedCurrency(currency || savedCurrency);
    const { exchangevalue, merchantMargin } = selectedCurrency;
    const formData = {
      country: country || savedCountry,
      currency: currency || savedCurrency,
      language: language || savedLanguage,
      savedCountry,
      savedLanguage,
      exchangevalue,
      merchantMargin,
    };
    handleSubmit(formData);
    this.closeModal();
  };

  changeCountry = (selectedCountry) => {
    const { updateCountry, updateSiteId } = this.props;
    if (selectedCountry) {
      const { siteId } = this.getSelectedCountry(selectedCountry);
      const currencyCode = this.getCurrencyMap(selectedCountry);
      this.updateCurrency(currencyCode);
      updateSiteId(siteId);
    }
    updateCountry(selectedCountry);
  };

  changeLanguage = (selectedLanguage) => {
    const { updateLanguage } = this.props;
    updateLanguage(selectedLanguage);
  };

  updateCurrency = (selectedCurrency) => {
    const { updateCurrency } = this.props;
    updateCurrency(selectedCurrency);
  };

  getLanguageMap = () => {
    const { siteId, sitesTable } = this.props;
    const siteLanguages = sitesTable[siteId];
    return siteLanguages && siteLanguages.languages;
  };

  getDisplayName = (language) => {
    const { isFooterRedesignEnabled, selectedCountryName } = this.props;
    const translatedNames = {
      spanish: 'Español',
      french: 'Français',
      english: 'English',
    };

    if (!language.displayName) {
      return '';
    }

    // Normalize the displayName to lower case
    const displayNameKey = language.displayName.toLowerCase();

    // Safely check if the displayName exists in the translatedNames map
    const displayNameExists = Object.prototype.hasOwnProperty.call(translatedNames, displayNameKey);

    // Use the translated name if it exists, otherwise use the displayName directly
    const displayText = displayNameExists ? translatedNames[displayNameKey] : language.displayName;

    if (isFooterRedesignEnabled) {
      return `${displayText?.toUpperCase()?.substring(0, 3)}${
        selectedCountryName ? `/${selectedCountryName?.toUpperCase()}` : ''
      }`;
    }

    return displayText.substring(0, 3);
  };

  showCountrySelectModal = (languages) => {
    const {
      countriesMap,
      currenciesMap,
      country,
      currency,
      isModalOpen,
      savedCountry,
      savedCurrency,
      savedLanguage,
      noteContent,
      sitesTable,
      siteId,
    } = this.props;

    return (
      <DynamicModule
        importCallback={() =>
          import(/* webpackChunkName: "country-selector-modal" */ './CountrySelectorModal')
        }
        isModalOpen={isModalOpen}
        closeModal={this.closeModal}
        countriesMap={countriesMap}
        currenciesMap={currenciesMap}
        languages={languages}
        noteContent={noteContent}
        savedCountry={savedCountry}
        savedCurrency={savedCurrency}
        savedLanguage={savedLanguage}
        handleSubmit={this.submitForm}
        updateCountry={this.changeCountry}
        updateLanguage={this.changeLanguage}
        updateCurrency={this.updateCurrency}
        updatedCountry={country}
        updatedCurrency={currency}
        initialValues={{
          country: savedCountry,
          language: savedLanguage,
          currency: savedCurrency,
        }}
        getCountryListData={this.getCountryListData}
        loadCountryModuleXData={this.loadCountryModuleXData}
        sitesTable={sitesTable}
        siteId={siteId}
      />
    );
  };

  render() {
    const {
      className,
      isModalOpen,
      savedCountry,
      savedLanguage,
      showInFooter,
      selectedCountryName,
      trackAnalyticsClick,
    } = this.props;
    const languages = this.getLanguageMap();
    const flagIconSrc = getFlagIconPath(savedCountry);
    const disablePopup = isSNJ();

    return (
      <div className={`${className} countrySelector`}>
        {isModalOpen && (
          <>
            {showInFooter && (
              <BodyCopy
                className="countrySelector__shipTo"
                color="gray.800"
                component="div"
                fontFamily="secondary"
                fontSize="fs12"
              >
                {renderLabel(
                  this.globalLabels?.countrySelector?.lbl_global_country_selector_header,
                  'lbl_global_country_selector_header'
                )}
              </BodyCopy>
            )}
            {this.showCountrySelectModal(languages)}
          </>
        )}
        <Anchor
          noLink
          handleLinkClick={(e) => {
            e.preventDefault();
            if (!disablePopup) {
              this.openModal();
            }
            trackAnalyticsClick(
              { nav_cd171: 'header-language-change', customEvents: ['event82'] },
              { name: 'header-language-change' }
            );
          }}
          className="county-selector-link"
          aria-expanded={false}
        >
          <div className="countrySelector__flag-icon">
            <Image
              src={flagIconSrc}
              alt={`${selectedCountryName} Flag`}
              width="20px"
              height="20px"
              onClick={disablePopup ? () => {} : this.openModal}
              data-locator={getLocator(
                showInFooter ? 'footer_country_flag' : 'header_country_flag'
              )}
            />
          </div>
          <div className="language-name">
            {languages &&
              languages.map(
                (language, index) =>
                  language.id === savedLanguage && (
                    <BodyCopy
                      key={index.toString()}
                      component="span"
                      fontFamily="secondary"
                      fontSize="fs13"
                      data-locator={
                        language.id === savedLanguage
                          ? getLocator(
                              showInFooter ? 'footer_language_selected' : 'header_language_selected'
                            )
                          : ''
                      }
                      className={`${
                        language.id === savedLanguage
                          ? 'countrySelector__locale--selected'
                          : 'countrySelector__locale--disabled'
                      } countrySelector__locale`}
                      aria-label={language.displayName}
                      aria-hidden={language.id !== savedLanguage}
                    >
                      {this.getDisplayName(language)}
                    </BodyCopy>
                  )
              )}
          </div>
        </Anchor>
      </div>
    );
  }
}

CountrySelector.propTypes = {
  className: PropTypes.string.isRequired,
  country: PropTypes.string,
  currency: PropTypes.string,
  language: PropTypes.string,
  noteContentId: PropTypes.string,
  noteContent: PropTypes.string,
  savedCountry: PropTypes.string,
  savedCurrency: PropTypes.string,
  savedLanguage: PropTypes.string,
  loadCountryListData: PropTypes.func,
  countriesMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currenciesMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getModuleXContent: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  isModalOpen: PropTypes.bool,
  showInFooter: PropTypes.bool,
  sitesTable: PropTypes.shape({}).isRequired,
  siteId: PropTypes.string,
  toggleModal: PropTypes.func,
  updateCountry: PropTypes.func,
  updateLanguage: PropTypes.func,
  updateCurrency: PropTypes.func,
  updateSiteId: PropTypes.func,
  selectedCountryName: PropTypes.string,
  trackAnalyticsClick: PropTypes.func,
  isFooterRedesignEnabled: PropTypes.bool,
};

CountrySelector.defaultProps = {
  showInFooter: false,
  handleSubmit: () => {},
  toggleModal: () => {},
  loadCountryListData: () => {},
  updateCountry: () => {},
  updateLanguage: () => {},
  updateCurrency: () => {},
  updateSiteId: () => {},
  noteContentId: '',
  savedCountry: '',
  savedCurrency: '',
  savedLanguage: '',
  siteId: '',
  trackAnalyticsClick: () => {},
  isFooterRedesignEnabled: false,
};

export default errorBoundary(withStyles(CountrySelector, style));
export { CountrySelector as CountrySelectorVanilla };
